const buildEmptyEmployment = () => {
  return {
    position: "",
    employer_name: "",
    employer_phone: "",
    gross_income: "",
    employment_status: "",
    start_date: {
      day: "",
      month: "",
      year: "",
    },
  };
};

const buildEmptyPreviousEmployment = (index) => {
  return {
    variable: index,
    position: "",
    employer_name: "",
    employer_phone: "",
    gross_income: "",
    employment_status: "",
    start_date: {
      day: "",
      month: "",
      year: "",
    },
    end_date: {
      day: "",
      month: "",
      year: "",
    },
  };
};

const ensureEmploymentShape = (employment = {}) => {
  const nextEmployment = {
    ...buildEmptyEmployment(),
    ...employment,
  };

  nextEmployment.start_date = {
    day: "",
    month: "",
    year: "",
    ...(employment.start_date || {}),
  };

  return nextEmployment;
};

const ensurePreviousEmploymentShape = (employment = {}, index = 1) => {
  const nextEmployment = {
    ...buildEmptyPreviousEmployment(index),
    ...employment,
  };

  nextEmployment.start_date = {
    day: "",
    month: "",
    year: "",
    ...(employment.start_date || {}),
  };

  nextEmployment.end_date = {
    day: "",
    month: "",
    year: "",
    ...(employment.end_date || {}),
  };

  return nextEmployment;
};

export const normalizeEmploymentDetails = (rawSection = {}) => {
  const normalized = {
    applicants: {},
    applicant_order: [],
    other_income_details: rawSection.other_income_details || "",
  };

  if (rawSection && rawSection.applicants && Object.keys(rawSection.applicants).length > 0) {
    const applicantOrder = Array.isArray(rawSection.applicant_order)
      ? rawSection.applicant_order.filter((id) => rawSection.applicants[id] !== undefined)
      : Object.keys(rawSection.applicants);

    applicantOrder.forEach((id) => {
      normalized.applicants[id] = normalizeApplicantEmployment(rawSection.applicants[id]);
    });
    normalized.applicant_order = applicantOrder;
  } else {
    const legacyApplicantKeys = Object.keys(rawSection || {}).filter((key) =>
      /^applicant\d+$/i.test(key)
    );

    legacyApplicantKeys.forEach((key) => {
      normalized.applicants[key] = normalizeApplicantEmployment(rawSection[key]);
      normalized.applicant_order.push(key);
    });
  }

  if (normalized.applicant_order.length === 0) {
    normalized.applicant_order = ["applicant1"];
    normalized.applicants.applicant1 = {
      current: buildEmptyEmployment(),
      previous: [],
    };
  }

  return normalized;
};

const normalizeApplicantEmployment = (rawApplicant = {}) => {
  const applicantEmployment = {
    current: ensureEmploymentShape(rawApplicant),
    previous: [],
  };

  const previousEmploymentKeys = Object.keys(rawApplicant)
    .filter((key) => key.startsWith("previousemployment"))
    .sort((a, b) => {
      const aNum = parseInt(a.replace("previousemployment_", ""));
      const bNum = parseInt(b.replace("previousemployment_", ""));
      return aNum - bNum;
    });

  previousEmploymentKeys.forEach((key, index) => {
    applicantEmployment.previous.push(ensurePreviousEmploymentShape(rawApplicant[key], index + 1));
  });

  return applicantEmployment;
};

export const buildPersistedEmploymentDetails = (normalizedSection) => {
  const persisted = {
    applicants: {},
    applicant_order: [...(normalizedSection.applicant_order || [])],
    other_income_details: normalizedSection.other_income_details || "",
  };

  (normalizedSection.applicant_order || []).forEach((id, index) => {
    const applicantData = normalizedSection.applicants[id];
    const persistedApplicant = {
      ...ensureEmploymentShape(applicantData.current),
    };

    applicantData.previous.forEach((prevEmployment, prevIndex) => {
      const key = `previousemployment_${prevIndex + 1}`;
      persistedApplicant[key] = ensurePreviousEmploymentShape(prevEmployment, prevIndex + 1);
    });

    persisted.applicants[id] = persistedApplicant;
    persisted[`applicant${index + 1}`] = persistedApplicant;
  });

  if (!persisted.applicant1) {
    persisted.applicant1 = {
      ...buildEmptyEmployment(),
    };
  }

  return persisted;
};

export const validateEmploymentHistory = (applicantEmployment, requiredYearsBack = 3) => {
  const targetYear = new Date().getFullYear() - (requiredYearsBack - 1);
  
  if (applicantEmployment.current.start_date && applicantEmployment.current.start_date.year) {
    const startYear = parseInt(applicantEmployment.current.start_date.year);
    if (startYear <= targetYear) {
      return true;
    }
  }

  for (const prevEmployment of applicantEmployment.previous) {
    if (prevEmployment.start_date && prevEmployment.start_date.year) {
      const startYear = parseInt(prevEmployment.start_date.year);
      if (startYear <= targetYear) {
        return true;
      }
    }
  }

  return false;
};
